import React, { useState } from 'react'
import type { StyleClasses, DialogProps } from '@paintscout/ui'
import {
  QuoteTagChip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputField,
  SwatchPicker
} from '@paintscout/ui'
import { TAG_COLORS } from '@paintscout/util'
import CloseIcon from '@material-ui/icons/Close'
import { updateQuoteTag } from '@paintscout/util/builder'
import type { OptionsDocument, QuoteTag } from 'paintscout'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import clone from 'lodash/clone'

export interface EditQuoteTagDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  item: QuoteTag
  options: OptionsDocument
  isNew?: boolean
  onConfirm?: (options: OptionsDocument, isDirty?: boolean) => void
  onCancel?: () => void
}

const useStyles = makeStyles<Theme, EditQuoteTagDialogProps>(
  (_theme) => ({
    swatchPicker: {
      margin: 0,
      maxWidth: 'unset',
      border: 'none',
      padding: 0
    },
    dialogTitle: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }
  }),
  { name: 'EditQuoteTagDialog' }
)

function EditQuoteTagDialog(props: EditQuoteTagDialogProps) {
  const classes = useStyles(props)
  const { loading, options, isNew, onConfirm, onCancel, item: initialItem, ...otherProps } = props

  const [item, setItem] = useState<QuoteTag>(() => clone(initialItem))
  const [isDirty, setIsDirty] = useState(false)

  const handleConfirm = () => {
    if (onConfirm) {
      const updatedOptions = updateQuoteTag({ options, quoteTag: item })
      onConfirm(updatedOptions, isDirty)
    }
  }

  const handleChangeLabel = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setItem((prev) => ({ ...prev, [name]: value }))
    setIsDirty(true)
  }

  const handleChangeColor = (newColor: string) => {
    setItem((prev) => ({ ...prev, color: newColor }))
    setIsDirty(true)
  }

  return (
    <Dialog fullWidth maxWidth="md" {...otherProps}>
      <DialogTitle
        classes={{ root: classes.dialogTitle }}
        rightContent={
          <IconButton size="small" onClick={onCancel} disabled={loading}>
            <CloseIcon />
          </IconButton>
        }
      >
        {`${isNew ? 'Add' : 'Edit'} Tag`}
      </DialogTitle>
      <DialogContent>
        <form
          id="area-option"
          onSubmit={(ev) => {
            ev.preventDefault()
            handleConfirm()
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <InputField
                classes={{ root: classes?.input }}
                label="Label"
                name="label"
                value={item.label}
                fullWidth
                onChange={handleChangeLabel}
                required
                error={!item.label}
              />
            </Grid>
            <Grid item xs={'auto'}>
              <SwatchPicker
                classes={{ root: classes.swatchPicker }}
                swatchSize={24}
                variant="inline"
                layout="row"
                colors={TAG_COLORS.map((pair) => pair[0])}
                onClick={handleChangeColor}
                currentColor={item.color || ''}
              />
            </Grid>
            {item.label && (
              <Grid item xs>
                <QuoteTagChip tag={item} handleDelete={() => console.log('im here for demo purposes')} />
              </Grid>
            )}
          </Grid>
        </form>
      </DialogContent>
      <DialogActions showBorder={true}>
        <Button type="submit" form="area-option" disabled={loading} variant="contained">
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EditQuoteTagDialog
